import React, { useState } from 'react';
import { ManagedImage } from '../ManagedImage';
import { FitControls } from './FitControls';

// Kort för en enskild portfolio-/galleribild i admin.
// props:
//  image, showCover, showTall, onSave(id, patch), onSetCover(image), onDelete(image)
export const ImageCard = ({ image, showCover = false, showTall = false, onSave, onSetCover, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => {
  const [title, setTitle] = useState(image.title || '');
  const [isTall, setIsTall] = useState(image.isTall);
  const [fitVal, setFitVal] = useState({ fit: image.fit, focusX: image.focusX, focusY: image.focusY });
  const [showFit, setShowFit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const imgProps = { url: image.url, basePath: image.basePath, filename: image.filename, alt: image.alt };

  const dirty =
    title !== (image.title || '') ||
    isTall !== image.isTall ||
    fitVal.fit !== image.fit ||
    fitVal.focusX !== image.focusX ||
    fitVal.focusY !== image.focusY;

  const save = async () => {
    setBusy(true);
    setStatus('');
    try {
      await onSave(image.id, { title, isTall, ...fitVal });
      setStatus('✓ Sparat');
    } catch (err) {
      setStatus('Fel: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cv-base border border-cv-border flex flex-col gap-3" style={{ borderRadius: 'var(--cv-radius)', padding: 14 }}>
      <div className="relative overflow-hidden bg-cv-panel" style={{ aspectRatio: '1/1', borderRadius: 'var(--cv-radius)' }}>
        <ManagedImage {...imgProps} fit={fitVal.fit} focusX={fitVal.focusX} focusY={fitVal.focusY} className="w-full h-full" />
        {showCover && image.isCover && (
          <span className="absolute top-2 left-2 text-[11px] tracking-[0.1em] uppercase text-cv-base bg-cv-ink px-2 py-1" style={{ borderRadius: 'var(--cv-radius)' }}>
            Omslag
          </span>
        )}
        {(onMoveUp || onMoveDown) && (
          <div className="absolute top-2 right-2 flex gap-1">
            <button type="button" onClick={() => onMoveUp(image)} disabled={!canMoveUp} aria-label="Flytta tidigare" className="w-8 h-8 flex items-center justify-center text-cv-ink border border-cv-edge cursor-pointer disabled:opacity-30 hover:bg-cv-line transition-colors" style={{ background: 'rgba(250,247,242,0.92)', borderRadius: 'var(--cv-radius)' }}>←</button>
            <button type="button" onClick={() => onMoveDown(image)} disabled={!canMoveDown} aria-label="Flytta senare" className="w-8 h-8 flex items-center justify-center text-cv-ink border border-cv-edge cursor-pointer disabled:opacity-30 hover:bg-cv-line transition-colors" style={{ background: 'rgba(250,247,242,0.92)', borderRadius: 'var(--cv-radius)' }}>→</button>
          </div>
        )}
      </div>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Bildtext (valfri)"
        className="px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-sm text-cv-ink outline-none focus:border-cv-accent"
      />

      {showTall && (
        <label className="flex items-center gap-2 text-sm text-cv-muted cursor-pointer">
          <input type="checkbox" checked={isTall} onChange={(e) => setIsTall(e.target.checked)} />
          Visa som stor (hög) bild
        </label>
      )}

      <button type="button" onClick={() => setShowFit((s) => !s)} className="w-fit text-xs tracking-[0.12em] uppercase text-cv-accent bg-transparent border-none cursor-pointer p-0 hover:underline">
        {showFit ? 'Dölj passning' : 'Passa in bild'}
      </button>
      {showFit && <FitControls imgProps={imgProps} value={fitVal} onChange={setFitVal} />}

      <div className="flex flex-wrap gap-2 items-center mt-auto pt-1">
        {dirty && (
          <button type="button" onClick={save} disabled={busy} className="bg-cv-ink text-cv-base border-none px-4 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">
            Spara
          </button>
        )}
        {showCover && !image.isCover && (
          <button type="button" onClick={() => onSetCover(image)} className="bg-transparent border border-cv-edge text-cv-ink px-4 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-cv-line transition-colors">
            Gör till omslag
          </button>
        )}
        <button type="button" onClick={() => onDelete(image)} className="bg-transparent border border-red-200 text-red-700 px-4 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-red-50 transition-colors ml-auto">
          Ta bort
        </button>
      </div>
      {status && <p className="m-0 text-xs text-cv-ink2">{status}</p>}
    </div>
  );
};
